import React, { useCallback, useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import SEOHead from "./SEOHead";
import { auth, googleProvider, appleProvider } from "../firebase";
import { onAuthStateChanged, signInWithPopup, signInWithRedirect, getRedirectResult, signOut } from "firebase/auth";
 

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL || "https://asia-southeast1-cuattendance.cloudfunctions.net/api";

const Premium = () => {
    const { colors } = useTheme();
    const [currentUser, setCurrentUser] = useState(null);
    const [showAuth, setShowAuth] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [pendingCheckout, setPendingCheckout] = useState(false);
    const [checkoutMode, setCheckoutMode] = useState(null); // 'onetime' | 'subscription'
    const [subscriptionStatus, setSubscriptionStatus] = useState({
        hasPremium: false,
        status: null,
        renewalDate: null,
        subscriptionId: null,
        loading: false,
        error: null
    });

    const fetchSubscriptionStatus = useCallback(async (user) => {
        if (!user) {
            setSubscriptionStatus({
                hasPremium: false,
                status: null,
                renewalDate: null,
                subscriptionId: null,
                loading: false,
                error: null
            });
            return;
        }

        setSubscriptionStatus(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const token = await user.getIdToken();
            const response = await fetch(`${FUNCTIONS_BASE_URL}/api/user/subscription-status`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Failed to fetch subscription status`);
            }

            const data = await response.json();
            setSubscriptionStatus({
                ...data,
                loading: false,
                error: null
            });
        } catch (err) {
            console.error("Failed to fetch subscription status:", err);
            setSubscriptionStatus(prev => ({
                ...prev,
                loading: false,
                error: "Failed to load subscription status"
            }));
        }
    }, []);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            // Fetch subscription status when user state changes
            fetchSubscriptionStatus(user);
        });
        return () => unsub();
    }, [fetchSubscriptionStatus]);

    useEffect(() => {
        // Handle redirect result if we returned from a redirect-based sign-in
        (async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result && result.user) {
                    setShowAuth(false);
                    // Fetch subscription status first, then check if checkout is needed
                    await fetchSubscriptionStatus(result.user);
                    if (checkoutMode) setPendingCheckout(true); // Only continue if a flow was chosen
                }
            } catch (_) {}
        })();
    }, [fetchSubscriptionStatus, checkoutMode]);

    const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);

    useEffect(() => {
        if (typeof window !== "undefined" && window.Razorpay) {
            setIsRazorpayLoaded(true);
            return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => setIsRazorpayLoaded(true);
        script.onerror = () => setIsRazorpayLoaded(false);
        document.body.appendChild(script);
        return () => {
            try {
                document.body.removeChild(script);
            } catch (_) {}
        };
    }, []);

    const handleLoginGoogle = useCallback(async () => {
        setErrorMessage("");
        try {
            const result = await signInWithPopup(auth, googleProvider);
            setShowAuth(false);
            // Fetch subscription status first, then set pending checkout
            await fetchSubscriptionStatus(result.user);
            if (checkoutMode) setPendingCheckout(true);
        } catch (err) {
            try {
                if (checkoutMode) setPendingCheckout(true);
                await signInWithRedirect(auth, googleProvider);
            } catch (_) {
                setErrorMessage("Failed to sign in with Google. Please try again.");
            }
        }
    }, [fetchSubscriptionStatus, checkoutMode]);

    const handleLoginApple = useCallback(async () => {
        setErrorMessage("");
        try {
            const result = await signInWithPopup(auth, appleProvider);
            setShowAuth(false);
            // Fetch subscription status first, then set pending checkout
            await fetchSubscriptionStatus(result.user);
            if (checkoutMode) setPendingCheckout(true);
        } catch (err) {
            try {
                if (checkoutMode) setPendingCheckout(true);
                await signInWithRedirect(auth, appleProvider);
            } catch (_) {
                setErrorMessage("Failed to sign in with Apple. Please try again.");
            }
        }
    }, [fetchSubscriptionStatus, checkoutMode]);

    const handleLogout = useCallback(async () => {
        setErrorMessage("");
        try {
            await signOut(auth);
        } catch (err) {
            setErrorMessage("Failed to sign out. Please try again.");
        }
    }, []);

    const handleCloseAuth = useCallback(() => {
        setShowAuth(false);
        setErrorMessage("");
        setPendingCheckout(false);
        setCheckoutMode(null);
    }, []);

    const startOneTimeCheckoutForUser = useCallback(async (userArg) => {
        const user = userArg || auth.currentUser || currentUser;
        if (!user) {
            setShowAuth(true);
            setPendingCheckout(true);
            return;
        }
        
        // Don't start checkout if user already has premium
        if (subscriptionStatus.hasPremium && !subscriptionStatus.loading) {
            setPendingCheckout(false);
            return;
        }
        if (typeof window === "undefined" || !isRazorpayLoaded || !window.Razorpay) {
            // Wait until Razorpay script loads; effect below will retry once available
            setPendingCheckout(true);
            return;
        }
        setIsLoading(true);
        try {
            const token = await user.getIdToken();
            
            // One-time order creation
            const createOrderResponse = await fetch(`${FUNCTIONS_BASE_URL}/api/razorpay/create-order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!createOrderResponse.ok) throw new Error("Failed to initialize order");
            const { orderId } = await createOrderResponse.json();

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID || "",
                order_id: orderId,
                name: "COLLEGIA Premium",
                description: "Ad‑Free Access (30 days)",
                image: "/assets/app_logo/SVG/IconForWhiteBackground.svg",
                prefill: {
                    name: user.displayName || "",
                    email: user.email || "",
                },
                notes: {
                    firebase_uid: user.uid,
                },
                theme: {
                    color: "#8b5cf6",
                },
                handler: async function (response) {
                    try {
                        const verifyResponse = await fetch(`${FUNCTIONS_BASE_URL}/api/razorpay/onetime/verify`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_signature: response.razorpay_signature,
                            }),
                        });
                        if (!verifyResponse.ok) throw new Error("Verification failed");
                        setErrorMessage("");
                        await fetchSubscriptionStatus(user);
                    } catch (e) {
                        setErrorMessage("Payment verification failed. Please contact support.");
                    } finally {
                        setIsLoading(false);
                    }
                },
                modal: {
                    ondismiss: () => {
                        setIsLoading(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", function () {
                setErrorMessage("Payment failed. Please try again.");
                setIsLoading(false);
            });
            setPendingCheckout(false);
            rzp.open();
        } catch (err) {
            setErrorMessage("Could not start checkout. Please try again.");
            setIsLoading(false);
        }
    }, [currentUser, isRazorpayLoaded, subscriptionStatus.hasPremium, subscriptionStatus.loading, fetchSubscriptionStatus]);

    const startSubscriptionCheckoutForUser = useCallback(async (userArg) => {
        const user = userArg || auth.currentUser || currentUser;
        if (!user) {
            setShowAuth(true);
            setPendingCheckout(true);
            return;
        }
        if (subscriptionStatus.hasPremium && !subscriptionStatus.loading) {
            setPendingCheckout(false);
            return;
        }
        if (typeof window === "undefined" || !isRazorpayLoaded || !window.Razorpay) {
            setPendingCheckout(true);
            return;
        }
        setIsLoading(true);
        try {
            const token = await user.getIdToken();

            const createSubResponse = await fetch(`${FUNCTIONS_BASE_URL}/api/razorpay/create-subscription`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    planId: process.env.REACT_APP_RAZORPAY_PLAN_ID,
                }),
            });
            if (!createSubResponse.ok) throw new Error("Failed to initialize subscription");
            const { subscriptionId } = await createSubResponse.json();

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY_ID || "",
                subscription_id: subscriptionId,
                name: "COLLEGIA Premium",
                description: "Ad‑Free Subscription (Monthly)",
                image: "/assets/app_logo/SVG/IconForWhiteBackground.svg",
                prefill: {
                    name: user.displayName || "",
                    email: user.email || "",
                },
                notes: {
                    firebase_uid: user.uid,
                },
                theme: {
                    color: "#8b5cf6",
                },
                handler: async function (response) {
                    try {
                        const verifyResponse = await fetch(`${FUNCTIONS_BASE_URL}/api/razorpay/verify`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_subscription_id: response.razorpay_subscription_id,
                                razorpay_signature: response.razorpay_signature,
                            }),
                        });
                        if (!verifyResponse.ok) throw new Error("Verification failed");
                        setErrorMessage("");
                        await fetchSubscriptionStatus(user);
                    } catch (e) {
                        setErrorMessage("Payment verification failed. Please contact support.");
                    } finally {
                        setIsLoading(false);
                    }
                },
                modal: {
                    ondismiss: () => {
                        setIsLoading(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", function () {
                setErrorMessage("Payment failed. Please try again.");
                setIsLoading(false);
            });
            setPendingCheckout(false);
            rzp.open();
        } catch (err) {
            setErrorMessage("Could not start checkout. Please try again.");
            setIsLoading(false);
        }
    }, [currentUser, isRazorpayLoaded, subscriptionStatus.hasPremium, subscriptionStatus.loading, fetchSubscriptionStatus]);

    const handleBuyOneTime = useCallback(async () => {
        setErrorMessage("");
        setCheckoutMode("onetime");
        if (!currentUser) {
            setShowAuth(true);
            setPendingCheckout(true);
            return;
        }
        await startOneTimeCheckoutForUser(currentUser);
    }, [currentUser, startOneTimeCheckoutForUser]);

    const handleSubscribeMonthly = useCallback(async () => {
        setErrorMessage("");
        setCheckoutMode("subscription");
        if (!currentUser) {
            setShowAuth(true);
            setPendingCheckout(true);
            return;
        }
        await startSubscriptionCheckoutForUser(currentUser);
    }, [currentUser, startSubscriptionCheckoutForUser]);

    useEffect(() => {
        // Only start checkout if user doesn't have premium and all conditions are met
        if (pendingCheckout && currentUser && isRazorpayLoaded && typeof window !== "undefined" && window.Razorpay && !subscriptionStatus.hasPremium && !subscriptionStatus.loading) {
            if (checkoutMode === "onetime") {
                startOneTimeCheckoutForUser(currentUser);
            } else if (checkoutMode === "subscription") {
                startSubscriptionCheckoutForUser(currentUser);
            }
        }
        // Clear pending checkout if user already has premium
        if (pendingCheckout && subscriptionStatus.hasPremium && !subscriptionStatus.loading) {
            setPendingCheckout(false);
        }
    }, [pendingCheckout, currentUser, isRazorpayLoaded, checkoutMode, startOneTimeCheckoutForUser, startSubscriptionCheckoutForUser, subscriptionStatus.hasPremium, subscriptionStatus.loading]);

    return (
        <>
            <SEOHead
                title="Collegia Premium — Ad‑Free Experience"
                description="Unlock a clean, distraction-free Collegia with Premium. Remove all ads to support ongoing development, app store fees, and server costs. Built by students, for students."
                keywords="Collegia premium, Collegia ad-free, remove ads, Collegia subscription, student app premium, CUIMS premium"
                url="https://collegia.app/premium"
            />
            <div className={`${colors.bg} min-h-screen py-12 px-4`}> 
            <div className="max-w-5xl mx-auto">
                {/* Hero */}
                <div className="text-center mb-14">
                    <img
                        src="/assets/app_logo/SVG/IconForWhiteBackground.svg"
                        className="w-20 h-20 lg:w-24 lg:h-24 rounded-2xl shadow-lg mx-auto mb-4"
                        alt="COLLEGIA App"
                    />
                    <h1 className={`text-4xl md:text-6xl font-light mb-4 ${colors.text}`}>Go Premium</h1>
                    <p className={`${colors.textSecondary} text-lg md:text-xl font-light max-w-2xl mx-auto`}>
                        Enjoy COLLEGIA with <span className={`${colors.text} font-semibold`}>zero ads</span>.
                        Minimal, fast, and distraction‑free.
                    </p>
                </div>

                {/* Pricing Card */}
                <div className="grid md:grid-cols-2 gap-8 items-stretch mb-16">
                    <div className={`rounded-3xl p-8 border ${colors.border} ${colors.card} backdrop-blur-sm relative overflow-hidden`}> 
                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-violet-500/20 to-cyan-500/20 rounded-full blur-2xl" />
                        <h2 className={`text-2xl font-medium mb-3 ${colors.text}`}>Ad‑Free COLLEGIA</h2>
                        <p className={`${colors.textSecondary} mb-6`}>Remove all in‑app advertisements for a calm, focused experience.</p>
                        <div className="flex items-end gap-2 mb-8">
                            <span className={`text-5xl font-light ${colors.text}`}>₹15</span>
                            <span className={`${colors.textMuted} mb-2`}>/ month</span>
                        </div>
                        <ul className={`space-y-3 ${colors.textSecondary} mb-8`}>
                            <li className="flex items-center gap-3"><span className="text-green-500">✓</span> Completely ad‑free interface</li>
                            <li className="flex items-center gap-3"><span className="text-green-500">✓</span> Fully unlocked Attendance Prediction</li>
                            <li className="flex items-center gap-3"><span className="text-green-500">✓</span> Directly supports developers</li>
                        </ul>
                        {currentUser && (
                            <div className="mb-4">
                                <p className={`${colors.textSecondary} text-xs uppercase tracking-wide mb-1`}>Logged in</p>
                                <div className="flex items-center justify-between text-sm">
                                    <span className={`${colors.text}`}>{currentUser.email || currentUser.uid}</span>
                                    <button onClick={handleLogout} className="text-violet-500 hover:text-violet-400 underline">
                                        sign out
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="space-y-3">
                            {currentUser && subscriptionStatus.hasPremium ? (
                                // Show subscription status when user has active premium
                                <div className="space-y-4">
                                    <div className={`p-4 rounded-xl border ${colors.border} bg-green-50 dark:bg-green-900/20`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3">
                                                <svg className="w-5 h-5 text-green-500 flex-shrink-0 relative top-[12px]" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                <h3 className={`font-semibold leading-tight ${colors.text}`}>Premium Active</h3>
                                            </div>
                                            <button
                                                onClick={() => fetchSubscriptionStatus(currentUser)}
                                                disabled={subscriptionStatus.loading}
                                                className={`text-xs px-2 py-1 rounded ${colors.buttonSecondary} hover:opacity-80 transition-opacity disabled:opacity-50`}
                                                title="Refresh subscription status"
                                            >
                                                {subscriptionStatus.loading ? '...' : '↻'}
                                            </button>
                                        </div>
                                        <p className={`text-sm ${colors.textSecondary} mb-2`}>
                                            You're currently enjoying COLLEGIA Premium with no ads!
                                        </p>
                                        {subscriptionStatus.renewalDate && (
                                            <p className={`text-xs ${colors.textMuted}`}>
                                                Expiry date: {new Date(subscriptionStatus.renewalDate).toLocaleDateString('en-IN', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </p>
                                        )}
                                        {subscriptionStatus.status && subscriptionStatus.status !== 'active' && (
                                            <p className={`text-xs text-amber-600 dark:text-amber-400 mt-1`}>
                                                Status: {subscriptionStatus.status.charAt(0).toUpperCase() + subscriptionStatus.status.slice(1)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                // Show purchase options when user doesn't have active premium
                                <div className="w-full grid grid-cols-1 gap-3">
                                    {/* <button onClick={handleSubscribeMonthly} disabled={isLoading || subscriptionStatus.loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-violet-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-60">
                                        <span>{isLoading ? 'Processing…' : subscriptionStatus.loading ? 'Checking Status…' : 'Subscribe'}</span>
                                        <span className="text-[10px] uppercase tracking-wide bg-white/15 text-white/90 px-2 py-0.5 rounded-full">Monthly</span>
                                    </button> */}
                                    <button onClick={handleBuyOneTime} disabled={isLoading || subscriptionStatus.loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-violet-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-60">
                                        <span>{isLoading ? 'Processing…' : subscriptionStatus.loading ? 'Checking Status…' : 'Buy 30-day Pass'}</span>
                                        <span className="text-[10px] uppercase tracking-wide bg-white/15 text-white/90 px-2 py-0.5 rounded-full">One‑time</span>
                                    </button>
                                </div>
                            )}
                            <a href="/" className={`w-full inline-flex items-center justify-center px-6 py-4 ${colors.buttonSecondary} font-semibold rounded-xl transition-colors duration-300`}>
                                Back to Home
                            </a>
                        </div>
                        {/* Inline auth buttons replaced by modal for improved UX */}
                        {errorMessage && <p className="text-red-500 text-sm mt-3">{errorMessage}</p>}
                        {subscriptionStatus.error && (
                            <div className="mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                <p className="text-red-600 dark:text-red-400 text-sm">
                                    {subscriptionStatus.error}
                                </p>
                                <button
                                    onClick={() => fetchSubscriptionStatus(currentUser)}
                                    className="text-red-600 dark:text-red-400 text-xs underline hover:no-underline mt-1"
                                >
                                    Try again
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Why monthly? + Important notes */}
                    <div className="flex flex-col gap-8">
                        <div className={`rounded-3xl p-8 border ${colors.border} ${colors.card} backdrop-blur-sm`}> 
                            <h3 className={`text-xl font-semibold mb-4 ${colors.text}`}>Why ₹15 per month?</h3>
                            <div className={`${colors.textSecondary} space-y-3 leading-relaxed`}>
                                <p>
                                    Premium helps cover <span className={`${colors.text} font-semibold`}>ongoing costs</span> so we can keep improving the app.
                                </p>
                                <ul className="space-y-2 ml-5 list-disc">
                                    <li>Development time and continuous updates</li>
                                    <li>App Store and Play Store listing & transaction fees</li>
                                    <li>Authentication and Hosting costs</li>
                                </ul>
                                <p>
                                    We are <span className={`${colors.text} font-semibold`}>independent student developers</span>; this small fee sustains the project
                                    and keeps the core experience accessible.
                                </p>
                            </div>
                        </div>

                        <div className={`rounded-3xl p-8 border ${colors.border} ${colors.card} backdrop-blur-sm`}> 
                            <h3 className={`text-xl font-semibold mb-4 ${colors.text}`}>Important notes</h3>
                            <div className={`${colors.textSecondary} space-y-3 leading-relaxed`}>
                                <ul className="space-y-2 ml-5 list-disc">
                                    <li>Sign in here with the same email ID used in the COLLEGIA app.</li>
                                    <li>After purchase, fully restart the COLLEGIA app to activate Premium.</li>
                                    <li>Your App must be updated to the latest version to use Premium.</li>
                                    {/* <li>Cancel premium anytime by removing autopay from Upi App.</li> */}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
            {showAuth && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={handleCloseAuth}>
                    <div className={`w-full max-w-md rounded-2xl p-6 md:p-8 border ${colors.border} ${colors.card} backdrop-blur-sm`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className={`text-xl font-semibold ${colors.text}`}>Sign in to continue</h3>
                            <button 
                                onClick={handleCloseAuth}
                                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${colors.textSecondary} hover:${colors.text}`}
                                aria-label="Close dialog"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p className={`${colors.textSecondary} text-sm mb-5`}>
                            Please sign in using the <span className={`font-medium ${colors.text}`}>same email ID</span> that you use in the COLLEGIA app. After sign‑in, checkout opens automatically.
                        </p>
                        <div className="space-y-3">
                            <button onClick={handleLoginGoogle} disabled={isLoading} className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-black text-white border border-gray-800 rounded-xl hover:shadow transition-all disabled:opacity-60">
                            <svg viewBox="-3 0 262 262" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" className="w-5 h-5" aria-hidden="true">
                                <path d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027" fill="#4285F4"></path>
                                <path d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1" fill="#34A853"></path>
                                <path d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782" fill="#FBBC05"></path>
                                <path d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251" fill="#EB4335"></path>
                            </svg>
                                <span>Sign In with Google</span>
                            </button>
                            <button onClick={handleLoginApple} disabled={isLoading} className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-black text-white border border-gray-800 rounded-xl hover:shadow transition-all disabled:opacity-60">
                            <svg viewBox="-52.01 0 560.035 560.035" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" aria-hidden="true" fill="currentColor">
                                <path d="M380.844 297.529c.787 84.752 74.349 112.955 75.164 113.314-.622 1.988-11.754 40.191-38.756 79.652-23.343 34.117-47.568 68.107-85.731 68.811-37.499.691-49.557-22.236-92.429-22.236-42.859 0-56.256 21.533-91.753 22.928-36.837 1.395-64.889-36.891-88.424-70.883-48.093-69.53-84.846-196.475-35.496-282.165 24.516-42.554 68.328-69.501 115.882-70.192 36.173-.69 70.315 24.336 92.429 24.336 22.1 0 63.59-30.096 107.208-25.676 18.26.76 69.517 7.376 102.429 55.552-2.652 1.644-61.159 35.704-60.523 106.559M310.369 89.418C329.926 65.745 343.089 32.79 339.498 0 311.308 1.133 277.22 18.785 257 42.445c-18.121 20.952-33.991 54.487-29.709 86.628 31.421 2.431 63.52-15.967 83.078-39.655"></path>
                            </svg>
                                <span>Sign In with Apple</span>
                            </button>
                            {/* <p className={`${colors.textSecondary} text-xs`}>
                                Apple sign in is not available now. Please use Google to sign in to purchase the subscription and use Google sign in in the app as well.
                            </p> */}
                        </div>
                        {errorMessage && <p className="text-red-500 text-sm mt-3">{errorMessage}</p>}
                    </div>
                </div>
            )}
        </>
    );
};

export default Premium;


